/**
 * API client functions for alert configuration and logs.
 */
import { api } from "@/lib/api-client"

// =============================================================================
// Types
// =============================================================================

export type AlertChannelConfig = {
  enabled: boolean
  cooldowns: Record<string, number>
  // Email-specific
  sender_email?: string
  recipients?: string[]
  // Webhook-specific
  url?: string
  headers?: Record<string, string>
}

export type AlertConfig = {
  enabled: boolean
  dry_run: boolean
  cooldowns: Record<string, number>
  channels: {
    email: AlertChannelConfig
    webhook: AlertChannelConfig
  }
  routing: Record<string, string[]>
  error_report_recipient: string
}

export type DeliveryMode = "sent" | "dry_run" | "log_only"

export type AlertLogEntry = {
  id: string
  log_type: string
  alert_type: string
  alert_key: string
  severity: string
  subject: string
  message: string
  channels_sent: string[]
  channels_failed: string[]
  delivered: boolean
  delivery_mode: DeliveryMode
  caller: string
  details: Record<string, unknown>
  created: string
}

export type AlertLogsResponse = {
  logs: AlertLogEntry[]
  count: number
}

/**
 * Incident lifecycle state.
 */
export type IncidentState = "active" | "stale" | "resolved"

/**
 * Grouped alert incident - represents multiple occurrences of the same alert_key.
 */
export type AlertIncident = {
  alert_key: string
  alert_type: string
  severity: string
  subject: string
  first_occurrence: string
  last_occurrence: string
  occurrence_count: number
  sample_id?: string
  delivery_mode?: DeliveryMode
  state: IncidentState
  lifetime_seconds?: number
  // Resolution info (only present for resolved incidents)
  resolved_at?: string
  resolved_by?: string
  resolved_note?: string
}

export type AlertSummaryResponse = {
  /** HTTP 200 may still report unavailable alert storage. */
  error?: string
  active: AlertIncident[]
  stale: AlertIncident[]
  resolved: AlertIncident[]
  counts: {
    active: number
    stale: number
    resolved: number
  }
  total_occurrences: number
  severity_counts: Record<string, number>
  period_days: number
}

export type ResolveIncidentResponse = {
  success: boolean
  message: string
}

export type TestAlertResponse = {
  success: boolean
  message: string
  delivered?: boolean
  delivery_mode?: DeliveryMode
  alert_id?: string
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get the current alert configuration.
 */
export async function getAlertConfig(): Promise<AlertConfig> {
  const response = await api.get("/admin/alerts/config")
  return response.data || response
}

/**
 * Update the alert configuration.
 */
export async function updateAlertConfig(config: Partial<AlertConfig>): Promise<AlertConfig> {
  const response = await api.put("/admin/alerts/config", config)
  return (response.data || response).config
}

/**
 * Update a specific channel's configuration.
 */
export async function updateAlertChannelConfig(
  channelId: "email" | "webhook",
  config: Partial<AlertChannelConfig>
): Promise<AlertChannelConfig> {
  const response = await api.put(`/admin/alerts/config/channels/${channelId}`, config)
  return (response.data || response).config
}

/**
 * Update the alert routing configuration.
 */
export async function updateAlertRouting(routing: Record<string, string[]>): Promise<void> {
  await api.put("/admin/alerts/config/routing", routing)
}

/**
 * Query alert logs with optional filters.
 */
export async function getAlertLogs(params?: {
  start_date?: string
  end_date?: string
  alert_type?: string
  severity?: string
  channel?: string
  limit?: number
  offset?: number
}): Promise<AlertLogsResponse> {
  const searchParams = new URLSearchParams()
  if (params?.start_date) searchParams.set("start_date", params.start_date)
  if (params?.end_date) searchParams.set("end_date", params.end_date)
  if (params?.alert_type) searchParams.set("alert_type", params.alert_type)
  if (params?.severity) searchParams.set("severity", params.severity)
  if (params?.channel) searchParams.set("channel", params.channel)
  if (params?.limit !== undefined) searchParams.set("limit", String(params.limit))
  if (params?.offset !== undefined) searchParams.set("offset", String(params.offset))

  const queryString = searchParams.toString()
  const url = queryString ? `/admin/alerts/logs?${queryString}` : "/admin/alerts/logs"

  const response = await api.get(url)
  return response.data || response
}

/**
 * Get recent alerts summary for dashboard (grouped by incident).
 */
export async function getRecentAlerts(days = 7, limit = 50): Promise<AlertSummaryResponse> {
  const response = await api.get(`/admin/alerts/recent?days=${days}&limit=${limit}`)
  return response.data || response
}

/**
 * Send a test alert.
 */
export async function sendTestAlert(params?: {
  severity?: "info" | "warning" | "critical"
}): Promise<TestAlertResponse> {
  const response = await api.post("/admin/alerts/test", params || {})
  return response.data || response
}

/**
 * Resolve an incident.
 */
export async function resolveIncident(
  alertKey: string,
  note?: string
): Promise<ResolveIncidentResponse> {
  const response = await api.post(
    `/admin/alerts/incidents/${encodeURIComponent(alertKey)}/resolve`,
    {
      note,
    }
  )
  return response.data || response
}

/**
 * Unresolve (re-open) an incident.
 */
export async function unresolveIncident(alertKey: string): Promise<ResolveIncidentResponse> {
  const response = await api.post(
    `/admin/alerts/incidents/${encodeURIComponent(alertKey)}/unresolve`
  )
  return response.data || response
}
